/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2026 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import styled from "styled-components";
import { useEffect } from "react";
import * as React from "react";
import { UserMetrics } from "components/ProjectIndex/useUserMetrics";
import accessIcon from "components/shared/images/premium-black-and-white.svg";
import { devices } from "components/shared/Styles";
import { useCurrentUser } from "actions/CurrentUserActions";
import { toPrintableTime } from "components/EnhancedSpeech/Utilities";

const EnhanceSpeechQuotaContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  width: calc(100% - 32px);
  margin-bottom: 24px;
  background-color: #f8f8f8;
  border-radius: 16px;
  padding: 15px;
  min-height: 75px;
  max-width: 230px;
  ${devices.small} {
    max-width: 100%;
  }
`;
const EnhancedSpeechTitle = styled.h3`
  font-weight: bold;
  font-size: 16px;
`;
const RowContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: 15px;
`;
const Icon = styled.img`
  width: 20px;
  height: 20px;

  ${devices.small} {
    width: 30px;
    height: 30px;
  }
`;
const QuotaTextLineContainer = styled.span`
  display: block;
`;

export const EnhanceSpeechQuota: React.FC<{
  isUserMetricsLoading: boolean;
  isFetchErrorOccurred: boolean;
  userMetrics: UserMetrics;
}> = ({ isUserMetricsLoading, isFetchErrorOccurred, userMetrics }) => {
  const { quotaResetTimeMins, quotaUsedMins, quotaLimitMins } = userMetrics;

  const quotaUsedPercentage =
    quotaLimitMins !== 0 ? (quotaUsedMins / quotaLimitMins) * 100 : 0;

  const { currentUser } = useCurrentUser();

  const quotaLimitString = (): string => {
    return I18n.t("login.quota.used_today", {
      used: toPrintableTime(quotaUsedMins),
      quota: toPrintableTime(quotaLimitMins),
    });
  };

  return (
    <EnhanceSpeechQuotaContainer>
      <RowContainer>
        <EnhancedSpeechTitle id="enhance-speech-quota-progress">
          {I18n.t("login.quota.enhance_speech_usage")}
        </EnhancedSpeechTitle>
        {currentUser?.hasPremiumAccess && (
          <Icon
            alt=""
            src={accessIcon}
            slot="icon"
            style={{
              position: "static",
              left: "1",
            }}
          />
        )}
      </RowContainer>
      <sp-progress-bar
        size={"s"}
        style={{
          position: "relative",
          width: "100%",
          bottom: "10px",
          height: "2px",
          margin: "6px 0 2px 0",
        }}
        progress={quotaUsedPercentage}
        // indeterminate property to be set to undefined in order for loading state to be disabled (not just false/falsy)
        indeterminate={isUserMetricsLoading ? true : undefined}
        aria-labelledby="enhance-speech-quota-progress"
      />
      <QuotaTextLineContainer
        style={{
          display:
            isUserMetricsLoading && !isFetchErrorOccurred ? "none" : "block",
        }}
      >
        {!isFetchErrorOccurred
          ? quotaLimitString()
          : I18n.t("login.quota.fetch_failed")}
      </QuotaTextLineContainer>
      <QuotaTextLineContainer
        style={{ display: isUserMetricsLoading ? "none" : "block" }}
      >
        {I18n.t("login.quota.reset_time", {
          quota_reset_time: toPrintableTime(quotaResetTimeMins),
        })}
      </QuotaTextLineContainer>
    </EnhanceSpeechQuotaContainer>
  );
};
